/**
 * `/errors` — 5xx, alerts, Telegram deliveries and the reference-counter table.
 *
 * ---------------------------------------------------------------------------
 * THE DELTA COLUMN
 * ---------------------------------------------------------------------------
 * The delta is a SIGNAL, not a number. `unproven` there means "we did not read
 * the live counter", which is a different thing from "the counter has not
 * moved" — and printing the second when we mean the first is the
 * reference-counter bug that Appendix B rule 14 exists to prevent. It therefore
 * renders in a dotted chip, in words, with no `+0` anywhere near it.
 *
 * ---------------------------------------------------------------------------
 * ROW BARS
 * ---------------------------------------------------------------------------
 * The 5xx tables draw a proportional bar behind each row. That is legal here and
 * only here: every value in `StatusRow` is a plain `number` on a signal that has
 * already been unwrapped as `ok`, so there is no row whose bar would be standing
 * in for an absence. Where the whole table is not `ok`, the panel is replaced by
 * the honest placeholder and no bars are drawn at all.
 */

import type { ReactNode } from 'react';

import { humanizeSeconds } from '../lib/format';
import { formatNumber } from '../lib/signal';
import type { Signal } from '../lib/signal';
import {
  Card,
  Eyebrow,
  Ident,
  KeyValue,
  Metric,
  Placeholder,
  SignalPanel,
  SignalValue,
  Skeleton,
  StatusPill,
} from '../components/primitives';
import { Icon } from '../components/icons';
import { SeriesChart } from '../components/SeriesChart';

interface StatusRow {
  namespace: string;
  ingress: string;
  /** The HTTP status. Named `httpStatus` so it cannot be mistaken for a signal
   *  status anywhere that walks this payload — see `api/types.ts#StatusRow`. */
  httpStatus: string;
  value: number;
}

interface RefRowView {
  name: string;
  value: string;
  updatedAt: string;
  ageSeconds: number;
  valueKind: 'counter' | 'timestamp' | 'text';
  delta: Signal<number>;
}

export interface ErrorsData {
  generatedAt: string;
  fiveXxLifetime: Signal<StatusRow[]>;
  fiveXxIncrease1h: Signal<StatusRow[]>;
  alertsBySeverity: Signal<Record<string, number>>;
  alertsActive: Signal<{ alertname: string; state: string; severity: string; count: number }[]>;
  telegramSends: Signal<Record<string, number>>;
  telegramFailed: Signal<Record<string, number>>;
  refs: RefRowView[];
}

const SEVERITY_ORDER = ['critical', 'warning', 'info', 'none'];

const SEVERITY_TONE: Record<string, 'bad' | 'warn' | 'neutral'> = {
  critical: 'bad',
  warning: 'warn',
  info: 'neutral',
  none: 'neutral',
};

export function ErrorsPage({ data }: { data: ErrorsData | null }): ReactNode {
  if (data === null) {
    return (
      <Skeleton label="the error and alert counters" shapes={['h-28', 'h-72']} />
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Alert severities, as the headline row. */}
      <SignalPanel signal={data.alertsBySeverity} emptyTitle="alert counts unavailable">
        {(counts) =>
          Object.keys(counts).length === 0 ? (
            <Placeholder status="empty" title="no active alerts" />
          ) : (
            <>
              <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {SEVERITY_ORDER.filter((s) => counts[s] !== undefined).map((severity) => (
                  <li key={severity}>
                    <Metric
                      label={severity}
                      value={formatNumber(counts[severity] ?? 0)}
                      tone={
                        (counts[severity] ?? 0) === 0
                          ? 'neutral'
                          : (SEVERITY_TONE[severity] ?? 'neutral')
                      }
                      sub="active alert series"
                      icon={severity === 'critical' ? 'errors' : 'warn'}
                    />
                  </li>
                ))}
              </ul>
              <p className="mt-3 flex items-center gap-2 text-2xs leading-relaxed text-ink-3">
                <Icon name="empty" size={13} />
                info alerts are blackholed — a non-zero info count is the steady state
              </p>
            </>
          )
        }
      </SignalPanel>

      <Card
        title="5xx movement"
        icon="errors"
        subtitle="the last hour, as increase(…[1h])"
        padding="flush"
      >
        <SignalPanel signal={data.fiveXxIncrease1h} emptyTitle="no 5xx movement to report">
          {(rows) => <StatusTable rows={rows} valueLabel="increase (1 h)" decimals={1} />}
        </SignalPanel>
      </Card>

      <Card title="5xx lifetime counters" icon="errors" padding="flush">
        <SignalPanel signal={data.fiveXxLifetime} emptyTitle="no lifetime 5xx counters">
          {(rows) => <StatusTable rows={rows} valueLabel="lifetime" decimals={0} />}
        </SignalPanel>
      </Card>

      <Card
        title="Active alert series"
        icon="warn"
        subtitle="from Alertmanager"
        right={<StatusPill status={data.alertsActive.status} />}
      >
        <SignalPanel signal={data.alertsActive} emptyTitle="no active alert series">
          {(rows) =>
            rows.length === 0 ? (
              <Placeholder status="empty" title="the query returned no alert series" />
            ) : (
              <ul className="space-y-1.5">
                {rows.map((row) => (
                  <li
                    key={`${row.alertname}/${row.state}/${row.severity}`}
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-surface-2 px-2.5 py-2"
                  >
                    <Ident className="min-w-0 flex-1 break-all leading-snug">{row.alertname}</Ident>
                    <span className="shrink-0 text-2xs text-ink-3">{row.state}</span>
                    <StatusPill
                      status={
                        row.severity === 'critical'
                          ? 'error'
                          : row.severity === 'warning'
                            ? 'unproven'
                            : 'empty'
                      }
                      size="sm"
                    >
                      {row.severity}
                    </StatusPill>
                  </li>
                ))}
              </ul>
            )
          }
        </SignalPanel>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card title="Telegram deliveries" subtitle="per Alertmanager pod">
          <SignalPanel signal={data.telegramSends} emptyTitle="no delivery counters">
            {(byPod) => <PodCounters byPod={byPod} />}
          </SignalPanel>
        </Card>
        <Card title="Telegram failures" subtitle="per Alertmanager pod">
          <SignalPanel signal={data.telegramFailed} emptyTitle="no failure counters">
            {(byPod) => <PodCounters byPod={byPod} badWhenPositive />}
          </SignalPanel>
        </Card>
      </div>

      <Card
        title="Reference counters"
        icon="clock"
        subtitle="stored values, and what moved since"
        padding="flush"
      >
        {data.refs.length === 0 ? (
          <div className="px-4 pb-4">
            <Placeholder status="empty" title="no references stored yet">
              the seed script and the sweep engine write them
            </Placeholder>
          </div>
        ) : (
          <>
            {/* A list below `sm`: the delta is the point and it must not be
                pushed off-screen by two identifier columns. */}
            <ul className="divide-y divide-line border-t border-line sm:hidden">
              {data.refs.map((ref) => (
                <li key={ref.name} className="px-4 py-2.5">
                  <Ident className="block break-all leading-snug">{ref.name}</Ident>
                  <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-2xs">
                    <span className="text-ink-3">
                      <span className="uppercase tracking-wide">{ref.valueKind}</span>
                      {' · stored '}
                      <span className="num text-ink-2">{ref.value}</span>
                      {' · '}
                      <span className="num">{humanizeSeconds(ref.ageSeconds)} old</span>
                    </span>
                    <span className="flex items-baseline gap-1.5">
                      <span className="text-ink-3">delta</span>
                      <SignalValue
                        signal={ref.delta}
                        format={(v) =>
                          (v as number) > 0
                            ? `+${formatNumber(v as number)}`
                            : formatNumber(v as number)
                        }
                        className={
                          ref.delta.status === 'ok' && (ref.delta.value as number) > 0
                            ? 'font-medium text-ink'
                            : undefined
                        }
                      />
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden border-t border-line sm:block">
              <table className="w-full text-left text-xs">
                <caption className="sr-only">
                  Stored reference counters, their age, and the movement since they were stored. A
                  delta that is not a number is printed as the reason it is not.
                </caption>
                <thead className="bg-surface-2 text-2xs uppercase tracking-[0.07em] text-ink-3">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 font-medium">reference</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">stored value</th>
                    <th scope="col" className="px-3 py-2.5 text-right font-medium">age</th>
                    <th scope="col" className="px-4 py-2.5 text-right font-medium">delta since</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.refs.map((ref) => (
                    <tr key={ref.name}>
                      <th scope="row" className="px-4 py-2.5 font-normal">
                        <Ident className="break-all">{ref.name}</Ident>
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-ink-3">
                          {ref.valueKind}
                        </span>
                      </th>
                      <td className="num px-3 py-2.5 text-right text-ink-2">{ref.value}</td>
                      <td className="num px-3 py-2.5 text-right text-ink-3">
                        {humanizeSeconds(ref.ageSeconds)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <SignalValue
                          signal={ref.delta}
                          format={(v) =>
                            (v as number) > 0
                              ? `+${formatNumber(v as number)}`
                              : formatNumber(v as number)
                          }
                          className={
                            ref.delta.status === 'ok' && (ref.delta.value as number) > 0
                              ? 'font-medium text-ink'
                              : undefined
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        <SeriesChart name="ingress_5xx_increase_1h" title="5xx movement" defaultRange="24h" maxSeries={8} />
        <SeriesChart name="telegram_notifications" title="Telegram deliveries" defaultRange="24h" />
      </div>
    </div>
  );
}

function PodCounters({
  byPod,
  badWhenPositive = false,
}: {
  byPod: Record<string, number>;
  badWhenPositive?: boolean;
}): ReactNode {
  const entries = Object.entries(byPod);
  if (entries.length === 0) {
    return <Placeholder status="empty" title="the query returned no pods" />;
  }
  return (
    <dl className="divide-y divide-line">
      {entries.map(([pod, value]) => (
        <KeyValue key={pod} label={pod}>
          <span
            className={`num ${badWhenPositive && value > 0 ? 'font-medium text-bad' : 'text-ink'}`}
          >
            {formatNumber(value)}
          </span>
        </KeyValue>
      ))}
    </dl>
  );
}

/**
 * The 5xx table.
 *
 * Sorted worst-first and given a proportional row bar, because "which of these
 * is the big one" is the only question asked of it and a column of numbers makes
 * you compute that yourself. The bar is scaled to the largest row, and the
 * number is always printed next to it.
 */
function StatusTable({
  rows,
  valueLabel,
  decimals,
}: {
  rows: StatusRow[];
  valueLabel: string;
  decimals: number;
}): ReactNode {
  if (rows.length === 0) {
    return (
      <div className="px-4 pb-4">
        <Placeholder status="empty" title="the query returned no rows">
          gate-proven, and legitimately nothing — not a count of zero
        </Placeholder>
      </div>
    );
  }
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  const max = sorted[0]?.value ?? 0;

  return (
    <>
      {/*
        Below `sm` this is a list, not a table.
        A four-column table with two long identifier columns needs ~470 px; at
        390 px it scrolled horizontally and put THE VALUE — the only reason
        anyone opens this panel — off the right-hand edge, behind a scroll
        gesture with no affordance. The list carries exactly the same four
        facts, worst first, with nothing off-screen.
      */}
      <ul className="divide-y divide-line border-t border-line sm:hidden">
        {sorted.map((row) => (
          <li key={`${row.namespace}/${row.ingress}/${row.httpStatus}`} className="px-4 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Ident className="block break-all leading-snug">{row.ingress}</Ident>
                <p className="mt-0.5 text-2xs text-ink-3">{row.namespace}</p>
              </div>
              <div className="flex shrink-0 items-baseline gap-2">
                <HttpStatus code={row.httpStatus} />
                <span className="num text-sm font-medium text-ink">
                  {formatNumber(row.value, decimals)}
                </span>
              </div>
            </div>
            <div
              className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/8"
              role="img"
              aria-label={`${valueLabel} ${formatNumber(row.value, decimals)}, the largest row is ${formatNumber(max, decimals)}`}
            >
              <span
                className="block h-full rounded-full bg-bad/70"
                style={{ width: `${String(max <= 0 ? 0 : (row.value / max) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden border-t border-line sm:block">
        <table className="w-full text-left text-xs">
          <caption className="sr-only">
            5xx counts by namespace, ingress and HTTP status, largest first.
          </caption>
          <thead className="bg-surface-2 text-2xs uppercase tracking-[0.07em] text-ink-3">
            <tr>
              <th scope="col" className="px-4 py-2.5 font-medium">namespace</th>
              <th scope="col" className="px-3 py-2.5 font-medium">ingress</th>
              <th scope="col" className="px-3 py-2.5 font-medium">status</th>
              <th scope="col" className="px-4 py-2.5 text-right font-medium">{valueLabel}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sorted.map((row) => (
              <tr key={`${row.namespace}/${row.ingress}/${row.httpStatus}`}>
                <th scope="row" className="px-4 py-2.5 font-normal text-ink-2">
                  {row.namespace}
                </th>
                <td className="px-3 py-2.5">
                  <Ident className="break-all">{row.ingress}</Ident>
                </td>
                <td className="px-3 py-2.5">
                  <HttpStatus code={row.httpStatus} />
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-2">
                    <span
                      aria-hidden="true"
                      className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-white/8"
                    >
                      <span
                        className="block h-full rounded-full bg-bad/70"
                        style={{ width: `${String(max <= 0 ? 0 : (row.value / max) * 100)}%` }}
                      />
                    </span>
                    <span className="num w-16 text-right text-ink">
                      {formatNumber(row.value, decimals)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function HttpStatus({ code }: { code: string }): ReactNode {
  return (
    <span
      className={`num rounded border px-1.5 text-2xs font-medium ${
        code.startsWith('5') ? 'epi-measured border-bad/45 text-bad' : 'border-line-strong text-ink-2'
      }`}
    >
      {code}
    </span>
  );
}

export { Eyebrow };
